import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { forkJoin, of, ReplaySubject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { createGuid, getAuthToken } from '../../../core/helpers/utility';
import { INotes, ICampaign } from '../../../core/models/campaigns.model';
import { ISalesRep } from '../../../core/models/datafeed.models';
import { ITask } from '../../../core/models/tasks.models';
import { ILead } from '../../../core/models/lead.models';
import { DataFeedService } from '../../api/datafeed.service';
import { TasksApiService } from '../../../pages/tasks/tasks.apiservice';
import { LeadsApiService } from '../../../pages/leads/leads.apiservice';
import { CampaignsApiService } from '../../../pages/campaigns/campaigns.apiservice';
import { ClientsApiService } from '../../../pages/clients/clients.apiservice';
import { SubscriptionApiService } from '../../../pages/subscriptions/subscription.apiservice';
import { OrdersApiService } from '../../../pages/orders/orders.apiservice';
import { NOTES_TYPE, ROLES_TYPE } from '../../../core/helpers/constants';
import { Utility } from '../../utility';
import * as moment from 'moment';
import { IUser } from '../../../core/models/auth.models';
import { TagsApiService } from '../../../pages/tags/tags.apiservice';
import { ITag } from '../../../core/models/tags.models';
import { AiRewriteModel, DashboardStyleSettingsService } from '../../services/dashboard-style-settings.service';
import { InstanceService } from '../../../core/instance/instance.service';

interface TarsReviewResult {
  campaignId: string;
  campaignName: string;
  response: any | null;
  error: string | null;
}

@Component({
  selector: 'modal-add-note',
  templateUrl: './add-campaigncheck.component.html',
  styleUrls: ['./add-campaigncheck.component.scss']
})
export class AddCampaignCheckModal implements OnInit, OnDestroy {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private readonly tarsAbortController = new AbortController();
  @Output() oSaveClicked = new EventEmitter<any>();
  @Output() oTaskUpdated = new EventEmitter<void>();

  allAssignees: Array<ISalesRep> = new Array<ISalesRep>();
  tasksDataNotCompleted: Array<ITask> = new Array<ITask>();
  campaignData: ICampaign = {} as ICampaign;
  campaignNotes: Array<INotes> = new Array<INotes>();
  clientNotes: Array<INotes> = new Array<INotes>();
  mostRecentOrder: any = {};
  orderData: any = {};
  adspendProduct: any[] = [];
  thisMonthsAdspend: number = 0;
  daysLeftInMonthIncludingToday: number = 0;
  leadsData: Array<ILead> = new Array<ILead>();
  activeSubscription: any = {};
  allUsers: Array<IUser> = new Array<IUser>();
  reportPurchasesEnabled = false;
  conversionActionBreakdown: Array<{ label: string; value: number }> = [];
  readonly aiRewriteModels: AiRewriteModel[] = ['gpt-5-mini', 'gpt-5', 'gpt-5.6-luna', 'gpt-5.6-terra'];

  // Client tier banner (mirrors logic from Client Details)
  isTiered: boolean = false;
  tierName: string = '';
  campaignTags: Array<ITag> = [];
  clientTags: Array<ITag> = [];

  get tierBannerClass(): string {
    const name = (this.tierName ?? '').toLowerCase();
    if (name.includes('basic')) return 'tier-banner--basic';
    if (name.includes('premium')) return 'tier-banner--premium';
    if (name.includes('standard')) return 'tier-banner--standard';
    return 'tier-banner--standard';
  }

  get metricCountLabel(): string {
    return this.reportPurchasesEnabled ? 'CONVERSION_ACTIONS' : 'LEADS';
  }

  get metricCostLabel(): string {
    return this.reportPurchasesEnabled ? 'CPA' : 'CPL';
  }

  get metricRateLabel(): string {
    return this.reportPurchasesEnabled ? 'CONVERSION_RATE' : 'LSR';
  }

  getOrderStatusKey(order: any): string {
    if (order?.paid && order?.isActive) return 'PAID';
    if (order?.paid && !order?.isActive) return 'VOIDED';
    if (!order?.paid && order?.isActive) return 'NOT_PAID';
    return 'CANCELED';
  }

  getOrderRefundStatusKey(order: any): string | null {
    const refundText = String(order?.refundText ?? '').toLowerCase();
    if (refundText.includes('fully refunded')) return 'FULLY_REFUNDED_PARENTHESIS';
    if (refundText.includes('partially refunded')) return 'PARTIALLY_REFUNDED_PARENTHESIS';
    return null;
  }

  get hasConversionActionBreakdown(): boolean {
    return this.reportPurchasesEnabled && (this.conversionActionBreakdown?.length ?? 0) > 0;
  }

  DateStart: string | null = null;
  DateStop: string | null = null;

  activeNotesSection: 'client' | 'campaign' | 'tasks' = 'campaign';
  activeExternalSection: 'metrics' | 'campaignAudit' | 'budgetAudit' | 'redAlertDeepDive' | 'tars' = 'metrics';
  readonly tarsMetricPeriods: Array<{ key: string; label: string; comparisonGroup: 'short_term' | 'long_term' }> = [
    { key: 'current_7_days', label: 'Last 7 Days', comparisonGroup: 'short_term' },
    { key: 'previous_7_days', label: 'Previous 7 Days', comparisonGroup: 'short_term' },
    { key: 'current_30_days', label: 'Last 30 Days', comparisonGroup: 'long_term' },
    { key: 'previous_30_days', label: 'Previous 30 Days', comparisonGroup: 'long_term' },
  ];
  readonly tarsMetricCards: Array<{ key: string; label: string; format: 'currency' | 'number' | 'percent' }> = [
    { key: 'spend', label: 'Spend', format: 'currency' },
    { key: 'impressions', label: 'Impressions', format: 'number' },
    { key: 'reach', label: 'Reach', format: 'number' },
    { key: 'leads', label: 'Leads', format: 'number' },
    { key: 'cpm', label: 'CPM', format: 'currency' },
    { key: 'ctr', label: 'CTR', format: 'percent' },
    { key: 'lsr', label: 'LSR', format: 'percent' },
    { key: 'cpl', label: 'CPL', format: 'currency' },
  ];

  get tarsMetricCardsForPlatform(): Array<{ key: string; label: string; format: 'currency' | 'number' | 'percent' }> {
    if (this.isGooglePlatform) {
      return [
        { key: 'spend', label: 'Spend', format: 'currency' },
        { key: 'impressions', label: 'Impressions', format: 'number' },
        { key: 'leads', label: 'Leads', format: 'number' },
        { key: 'cpc', label: 'CPC', format: 'currency' },
        { key: 'ctr', label: 'CTR', format: 'percent' },
        { key: 'lsr', label: 'LSR', format: 'percent' },
        { key: 'cpl', label: 'CPL', format: 'currency' },
      ];
    }
    return this.tarsMetricCards;
  }
  tarsLoading = false;
  tarsLoaded = false;
  tarsError: string | null = null;
  tarsResults: TarsReviewResult[] = [];
  activeTarsResultIndex = 0;

  // Lazy-mount Budget Audit: create component on first visit, then keep it alive.
  budgetAuditInitialized = false;
  private clientNotesLoaded = false;
  private upcomingTasksLoaded = false;

  highlightCampaignAuditTab = false;

  campaignAuditChecklist: Array<{ key: string; label: string; englishLabel: string }> = [
    { key: 'auditCompletedBudgetAudit', label: 'COMPLETED_BUDGET_AUDIT', englishLabel: 'Completed budget audit' },
    { key: 'auditCheckedSubscriptionAndOrder', label: 'CHECKED_SUBSCRIPTION_AND_ORDER', englishLabel: 'Checked subscription and order' },
    { key: 'auditCorrectOffer', label: 'ADS_ARE_USING_THE_CORRECT_OFFER', englishLabel: 'Ads are using the correct offer' },
    { key: 'auditCopyAndCreativeCorrect', label: 'COPY_AND_CREATIVE_ARE_CORRECT', englishLabel: 'Copy and creative are correct' },
    { key: 'auditTargetingCorrect', label: 'TARGETING_IS_CORRECT', englishLabel: 'Targeting is correct (keywords, location, demographics, etc.)' },
    { key: 'auditNamingConventionsCorrect', label: 'CHECK_NAMING_CONVENTIONS', englishLabel: 'Naming conventions are correct for all campaigns, ad sets, and ads' },
    { key: 'auditLandingPageCorrect', label: 'THE_LP_LEAD_FORM_IS_CORRECT', englishLabel: 'The LP / lead form is correct' },
    { key: 'auditLeadsInReach', label: 'LEADS_ARE_ROUTING_CORRECTLY_INTO_REACH', englishLabel: 'Leads are routing correctly into REACH' },
  ];

  private tryGetMeaningfulNoteDate(note: any): Date | null {
    // Prefer createdOn, but older notes may have DateTime.MinValue (0001-01-01) or null.
    const createdRaw = note?.createdOn ?? null;
    const created = createdRaw ? new Date(createdRaw) : null;
    if (created && !isNaN(created.getTime()) && created.getFullYear() >= 2000) {
      return created;
    }

    const updatedRaw = note?.updatedOn ?? null;
    const updated = updatedRaw ? new Date(updatedRaw) : null;
    if (updated && !isNaN(updated.getTime()) && updated.getFullYear() >= 2000) {
      return updated;
    }

    return null;
  }

  get mostRecentCampaignAuditNoteDate(): Date | null {
    const notes = this.campaignNotes ?? [];
    let best: Date | null = null;

    for (const note of notes) {
      const title = ((note as any)?.title ?? '').toString();
      if (!title.toLowerCase().includes('campaign audit')) continue;

      const dt = this.tryGetMeaningfulNoteDate(note);
      if (!dt) continue;
      if (!best || dt.getTime() > best.getTime()) {
        best = dt;
      }
    }

    return best;
  }

  get mostRecentRedAlertDeepDiveNoteDate(): Date | null {
    const notes = this.campaignNotes ?? [];
    let best: Date | null = null;

    for (const note of notes) {
      const title = ((note as any)?.title ?? '').toString();
      if (!title.toLowerCase().includes('red alert deep dive')) continue;

      const dt = this.tryGetMeaningfulNoteDate(note);
      if (!dt) continue;
      if (!best || dt.getTime() > best.getTime()) {
        best = dt;
      }
    }

    return best;
  }

  private hasNoteWithTitleInMonth(
    monthRefLocal: moment.Moment,
    titleMatcher: (title: string) => boolean
  ): boolean {
    const notes = this.campaignNotes ?? [];
    return notes.some((note: any) => {
      const title = String(note?.title ?? '').toLowerCase();
      if (!titleMatcher(title)) return false;

      const dt = this.tryGetMeaningfulNoteDate(note);
      if (!dt) return false;

      return moment.utc(dt).local().isSame(monthRefLocal, 'month');
    });
  }

  private evaluateCampaignAuditTabDefault(): void {
    const nowLocal = moment.utc().local();
    const previousMonthLocal = nowLocal.clone().subtract(1, 'month');

    const hasCampaignCheckLastMonth = this.hasNoteWithTitleInMonth(
      previousMonthLocal,
      (title) => title.includes('campaign check')
    );

    const hasCampaignAuditThisMonth = this.hasNoteWithTitleInMonth(
      nowLocal,
      (title) => title.includes('campaign audit')
    );

    const shouldHighlight = hasCampaignCheckLastMonth && !hasCampaignAuditThisMonth;

    this.highlightCampaignAuditTab = shouldHighlight;
    if (shouldHighlight) {
      this.activeExternalSection = 'campaignAudit';
    }
  }

  // Red Alert Deep Dive (Red Alert tab)
  // Two sections: Deep Dive + Actions Taken.
  readonly redAlertDeepDiveChecklist: Array<{ key: string; label: string; englishLabel: string }> = [
    {
      key: 'radDdFullReviewCampaignSetup',
      label: 'DO_A_FULL_CAMPAIGN_REVIEW',
      englishLabel: 'Do a full review of the campaign to make sure the budget is correct, location targeting is correct, ads are correct, copy and images are correct, etc.'
    },
    {
      key: 'radDdCheckMetricsDateRanges',
      label: 'CHECK_CAMPAIGN_METRICS',
      englishLabel: 'Check the campaign metrics and look at the last 7 days, last 30 days, and last 3 months, or potentially longer. Locate when it was doing well and then when it stopped doing well.'
    },
    {
      key: 'radDdCheckChangeHistoryAndNotes',
      label: 'CHECK_CHANGE_HISTORY',
      englishLabel: 'Go into the change history and see what, if anything, was changed on or around the date it started doing poorly. Check the REACH notes for the campaign to see what was done around that date.'
    },
    {
      key: 'radDdUndoChangesToTargetingEtc',
      label: 'UNDO_CHANGES',
      englishLabel: 'If there is a change to targeting, bid strategy, demographic targeting, budget changes, etc., this would need to be undone to see if that was the reason the campaign started trending downwards.'
    },
    {
      key: 'radDdConditionalClientRequestedChange',
      label: 'IF_CLIENT_CORPORATE_REQUESTED_THE_CHANGE',
      englishLabel: "Conditional: if the client or corporate specifically requested the change, find out if we can revert it. If it can't be reverted, continue with the following steps and fix what you can."
    },
    {
      key: 'radDdCheckLocationTargeting',
      label: 'CHECK_LOCATION_TARGETING',
      englishLabel: "Check over the location targeting. Make sure it's not interfering with any other studio. Check to see if there is room for expansion or if it's too large of a targeting area."
    },
    {
      key: 'radDdVerifyLeadsInReach',
      label: 'CHECK_REACH_LEADS',
      englishLabel: 'For campaigns where leads route into REACH, verify that leads are showing up inside REACH. Compare these numbers to what is reported in the platform, Google or Meta.'
    },
    {
      key: 'radDdCheckLeadFormOrLandingPage',
      label: 'CHECK_LEAD_ROUTING',
      englishLabel: "If there is a discrepancy with the leads in REACH and the platform, or leads don't route into REACH, check the lead form or landing page to ensure it is correct."
    },
    {
      key: 'radDdCoordinateTechServicesQc',
      label: 'CHECK_CONVERSION_REPORTING_AND_INTEGRATIONS',
      englishLabel: 'If needed, coordinate with Tech Services or QC to check conversion reporting and integrations.'
    },
    {
      key: 'radDdReviewTrendingIsolateConcern',
      label: 'REVIEW_CAMPAIGN_METRICS',
      englishLabel: 'Review the metrics for the campaign and look at all the main metrics and their trending over the date ranges mentioned above. Isolate the main metrics of concern.'
    },
    {
      key: 'radDdRecommendOrChangeCampaign',
      label: 'RECOMMEND_KPI_CHANGE',
      englishLabel: 'Once the metric of concern is isolated, recommend changes to the campaign to improve that metric.'
    },
  ];

  readonly redAlertRevertedPreviousChangesChecklist: Array<{ key: string; label: string; englishLabel: string }> = [
    { key: 'radActRevertedLocationTargeting', label: 'LOCATION_TARGETING', englishLabel: 'Location Targeting' },
    { key: 'radActRevertedDemographics', label: 'DEMOGRAPHICS', englishLabel: 'Demographics' },
    { key: 'radActRevertedKeywords', label: 'KEYWORDS', englishLabel: 'Keywords' },
    { key: 'radActRevertedBidStrategy', label: 'BID_STRATEGY', englishLabel: 'Bid Strategy' },
    { key: 'radActRevertedBudget', label: 'BUDGET', englishLabel: 'Budget' },
    { key: 'radActRevertedOther', label: 'OTHER', englishLabel: 'Other' },
  ];

  readonly redAlertKpiProblemChecklist: Array<{ key: string; label: string; englishLabel: string }> = [
    { key: 'radActKpiCtr', label: 'CTR', englishLabel: 'CTR' },
    { key: 'radActKpiLsr', label: 'LSR', englishLabel: 'LSR' },
    { key: 'radActKpiCpc', label: 'CPC', englishLabel: 'CPC' },
    { key: 'radActKpiCpm', label: 'CPM', englishLabel: 'CPM' },
    { key: 'radActKpiOther', label: 'OTHER', englishLabel: 'Other' },
  ];

  addCampaignCheckForm!: FormGroup;

  submitted = false;
  isGooglePlatform: boolean | null = null;

  @Input() iClientId: string = '';
  @Input() iCampaignId: string = '';
  @Input() iLeadId: string = '';
  @Input() iType: number = 1;
  @Input() iActiveUserExternal: number = ROLES_TYPE.External;
  @Input() iIsAdmin: boolean = true;
  @Input() iTask: ITask | null = null;
  @Input() set iNote(note: INotes) {
    if (note) {
      // Patch only keys that exist on the current form (older notes may contain legacy fields).
      const patch: any = {};
      for (const key of Object.keys(note as any)) {
        if ((this.addCampaignCheckForm as any)?.controls?.[key]) {
          patch[key] = (note as any)[key];
        }
      }
      this.addCampaignCheckForm.patchValue(patch);
      if (note.noteFiles)
        this.files = note.noteFiles;
    }
  }

  constructor(
    private dataFeedService: DataFeedService,
    private taskApiService: TasksApiService,
    private campaignsApiService: CampaignsApiService,
    private clientService: ClientsApiService,
    private subscriptionService: SubscriptionApiService,
    private orderApiService: OrdersApiService,
    private leadsApi: LeadsApiService,
    private tagService: TagsApiService,
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private dashboardStyleSettingsService: DashboardStyleSettingsService,
    private instanceService: InstanceService) {
    this.addCampaignCheckForm = this.formBuilder.group({
      id: [null],
      type: [null],
      campaignId: [null],
      campaignName: [null],
      clientId: [null],
      clientFirstName: [null],
      clientLastName: [null],
      leadId: [null],
      dueDate: [null],
      assignedUserId: [null],
      assignedUserFirstName: [null],
      assignedUserLastName: [null],
      impression: [null, [Validators.required]],
      leads: [null, [Validators.required]],
      cpl: [null, [Validators.required]],
      ctr: [null, [Validators.required]],
      cPMOrCPC: [null, [Validators.required]],
      lFSROrLPCR: [null, [Validators.required]],
      taskIdToComplete: [null],
      insightText: [null, [Validators.required]],
      changesText: [null, [Validators.required]],
      isQCCheck: [null],
      isAIEmail: [true],
      aiRewriteModel: ['gpt-5'],
      isRedAlert: [null],
      addTaskDate:[moment.utc().local().add(7, 'days').startOf('day').toDate(), [Validators.required]],
      nextTaskAssignedUserId: [null],

      // Campaign Audit checklist (optional)
      auditCompletedBudgetAudit: [false],
      auditCheckedSubscriptionAndOrder: [false],
      auditCorrectOffer: [false],
      auditCopyAndCreativeCorrect: [false],
      auditTargetingCorrect: [false],
      auditNamingConventionsCorrect: [false],
      auditLandingPageCorrect: [false],
      auditLeadsInReach: [false],
      auditAdditionalInfo: [''],

      // Red Alert Deep Dive — Deep Dive section (optional)
      radDdFullReviewCampaignSetup: [false],
      radDdCheckMetricsDateRanges: [false],
      radDdCheckChangeHistoryAndNotes: [false],
      radDdUndoChangesToTargetingEtc: [false],
      radDdConditionalClientRequestedChange: [false],
      radDdCheckLocationTargeting: [false],
      radDdVerifyLeadsInReach: [false],
      radDdCheckLeadFormOrLandingPage: [false],
      radDdCoordinateTechServicesQc: [false],
      radDdReviewTrendingIsolateConcern: [false],
      radDdRecommendOrChangeCampaign: [false],
      radDdAdditionalInfo: [''],

      // Red Alert Deep Dive — Actions Taken section (optional)
      radActFixedConversionTracking: [false],
      radActRevertedPreviousChanges: [false],
      radActRevertedLocationTargeting: [false],
      radActRevertedDemographics: [false],
      radActRevertedKeywords: [false],
      radActRevertedBidStrategy: [false],
      radActRevertedBudget: [false],
      radActRevertedOther: [false],
      radActRevertedOtherText: [''],
      radActKpiProblemFoundAndCorrected: [false],
      radActKpiCtr: [false],
      radActKpiLsr: [false],
      radActKpiCpc: [false],
      radActKpiCpm: [false],
      radActKpiOther: [false],
      radActKpiOtherText: [''],
      radActSentToStrategyForReview: [false],
      radActAdditionalInfo: [''],
    });
  }
  get f() { return this.addCampaignCheckForm.controls; }

  private getCheckedCampaignAuditItems(): string[] {
    const items: string[] = [];
    for (const entry of this.campaignAuditChecklist) {
      const isChecked = !!this.addCampaignCheckForm?.controls?.[entry.key]?.value;
      if (isChecked) {
        items.push(entry.englishLabel);
      }
    }
    return items;
  }

  private getCheckedRedAlertDeepDiveItems(): string[] {
    const items: string[] = [];

    // Deep Dive
    for (const entry of this.redAlertDeepDiveChecklist) {
      const isChecked = !!this.addCampaignCheckForm?.controls?.[entry.key]?.value;
      if (isChecked) items.push(entry.englishLabel);
    }

    const ddNotes = String(this.addCampaignCheckForm?.controls?.['radDdAdditionalInfo']?.value ?? '').trim();
    if (ddNotes) {
      items.push(`Additional information regarding findings: ${ddNotes}`);
    }

    // Actions Taken
    if (!!this.addCampaignCheckForm?.controls?.['radActFixedConversionTracking']?.value) {
      items.push('Fixed Conversion Tracking');
    }

    if (!!this.addCampaignCheckForm?.controls?.['radActRevertedPreviousChanges']?.value) {
      const selected: string[] = [];
      for (const entry of this.redAlertRevertedPreviousChangesChecklist) {
        const isChecked = !!this.addCampaignCheckForm?.controls?.[entry.key]?.value;
        if (!isChecked) continue;

        if (entry.key === 'radActRevertedOther') {
          const otherText = String(this.addCampaignCheckForm?.controls?.['radActRevertedOtherText']?.value ?? '').trim();
          selected.push(otherText ? `Other (${otherText})` : 'Other');
        } else {
          selected.push(entry.englishLabel);
        }
      }
      items.push(selected.length > 0
        ? `Reverted Previous Changes: ${selected.join(', ')}`
        : 'Reverted Previous Changes');
    }

    if (!!this.addCampaignCheckForm?.controls?.['radActKpiProblemFoundAndCorrected']?.value) {
      const selected: string[] = [];
      for (const entry of this.redAlertKpiProblemChecklist) {
        const isChecked = !!this.addCampaignCheckForm?.controls?.[entry.key]?.value;
        if (!isChecked) continue;

        if (entry.key === 'radActKpiOther') {
          const otherText = String(this.addCampaignCheckForm?.controls?.['radActKpiOtherText']?.value ?? '').trim();
          selected.push(otherText ? `Other (${otherText})` : 'Other');
        } else {
          selected.push(entry.englishLabel);
        }
      }
      items.push(selected.length > 0
        ? `KPI Problem Found and Corrected: ${selected.join(', ')}`
        : 'KPI Problem Found and Corrected');
    }

    if (!!this.addCampaignCheckForm?.controls?.['radActSentToStrategyForReview']?.value) {
      items.push('Sent to Strategy for Review');
    }

    const actNotes = String(this.addCampaignCheckForm?.controls?.['radActAdditionalInfo']?.value ?? '').trim();
    if (actNotes) {
      items.push(`Additional information about actions taken: ${actNotes}`);
    }

    return items;
  }

  activeUserId = '';
  assignedUserFullname = '';

  private patchHiddenIdentityFields(): void {
    const campaignName =
      (this.campaignData as any)?.campaignName ??
      this.campaignData?.name ??
      (this.iTask as any)?.campaignName ??
      null;

    const clientFirstName =
      (this.campaignData as any)?.client?.firstName ??
      (this.campaignData as any)?.clientFirstName ??
      null;

    const clientLastName =
      (this.campaignData as any)?.client?.lastName ??
      (this.campaignData as any)?.clientLastName ??
      null;

    // Assigned user: prefer full user list when available, otherwise fall back to parsing assignedUserName.
    const assignedUserId =
      this.addCampaignCheckForm?.controls?.['assignedUserId']?.value ??
      (this.iTask as any)?.assignedUserId ??
      null;

    const assignedUser = assignedUserId ? (this.allUsers ?? []).find(u => u.id === assignedUserId) : null;

    const parsedAssignedName = (((this.iTask as any)?.assignedUserName ?? '') as string).trim();
    const parsedParts = parsedAssignedName ? parsedAssignedName.split(/\s+/).filter(Boolean) : [];
    const parsedFirst = parsedParts.length > 0 ? parsedParts[0] : null;
    const parsedLast = parsedParts.length > 1 ? parsedParts.slice(1).join(' ') : null;

    const assignedUserFirstName =
      (assignedUser as any)?.firstName ??
      this.addCampaignCheckForm?.controls?.['assignedUserFirstName']?.value ??
      parsedFirst;

    const assignedUserLastName =
      (assignedUser as any)?.lastName ??
      this.addCampaignCheckForm?.controls?.['assignedUserLastName']?.value ??
      parsedLast;

    this.addCampaignCheckForm.patchValue(
      {
        campaignName,
        clientFirstName,
        clientLastName,
        assignedUserFirstName,
        assignedUserLastName,
      },
      { emitEvent: false }
    );
  }

  taskUpdateSaving = false;
  taskUpdateSuccess = false;
  taskUpdateError: string | null = null;

  ngOnInit() {
    this.dashboardStyleSettingsService.watch('dms')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        const control = this.addCampaignCheckForm.get('aiRewriteModel');
        if (control && !control.dirty && control.value !== state.aiRewriteModel) {
          control.setValue(state.aiRewriteModel, { emitEvent: false });
        }
      });

    this.addCampaignCheckForm.get('aiRewriteModel')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((model: AiRewriteModel) => {
        if (this.aiRewriteModels.includes(model)) {
          void this.dashboardStyleSettingsService.updateSettings('dms', { aiRewriteModel: model });
        }
      });

    const nowLocal = moment.utc().local();
    this.daysLeftInMonthIncludingToday = Math.max(1, nowLocal.daysInMonth() - nowLocal.date() + 1);

    setTimeout(() => {
      this.patchTaskFieldsFromTrigger();
      this.loadDetails();

      if (!this.iCampaignId) return;
      this.dataFeedService.getAllTaskAssignees()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          if (response.good) {
            this.allUsers = [...response.data].map((i) => { i.fullName = i.firstName + ' ' + i.lastName; return i; });
            this.patchTaskFieldsFromTrigger();
          }
        });
    }, 0);
  }

  setActiveNotesSection(section: 'client' | 'campaign' | 'tasks'): void {
    this.activeNotesSection = section;
    if (section === 'client' && !this.clientNotesLoaded) {
      this.loadClientNotes();
    }
    if (section === 'tasks' && !this.upcomingTasksLoaded) {
      this.loadUpcomingTasks();
    }
  }

  setActiveExternalSection(section: 'metrics' | 'campaignAudit' | 'budgetAudit' | 'redAlertDeepDive' | 'tars'): void {
    this.activeExternalSection = section;

    if (section === 'budgetAudit') {
      this.budgetAuditInitialized = true;
    }
    if (section === 'tars') {
      if (this.tarsLoaded || this.tarsLoading) return;
      void this.loadTarsReview();
    }
  }

  setActiveTarsResult(index: number): void {
    this.activeTarsResultIndex = index;
  }

  getTarsSectionData(response: any, section: string): any {
    if (['summary', 'metrics', 'recommendations', 'ads', 'reach_notes', 'operational_history', 'feedback'].includes(section)) {
      // TARS v1 returns sections under `sections`; older compatible builds
      // returned the same objects at the top level (and some gateways wrap
      // the payload in `data`). Accept all three shapes so a platform-specific
      // response cannot make the entire review appear blank.
      return response?.sections?.[section]
        ?? response?.[section]
        ?? response?.data?.sections?.[section]
        ?? response?.data?.[section]
        ?? response?.result?.sections?.[section]
        ?? response?.result?.[section]
        ?? response?.response?.sections?.[section]
        ?? response?.response?.[section]
        ?? response?.data?.response?.sections?.[section]
        ?? response?.data?.response?.[section];
    }
    return response?.[section];
  }

  private normalizeTarsResponse(data: any): any {
    if (!data || typeof data !== 'object') return data;
    const candidates = [
      data?.sections,
      data,
      data?.data?.sections,
      data?.data,
      data?.result?.sections,
      data?.result,
      data?.response?.sections,
      data?.response,
      data?.data?.response?.sections,
      data?.data?.response,
      data?.review?.sections,
      data?.review,
    ];
    const sectionSource = candidates.find(candidate => candidate && typeof candidate === 'object' && (
      candidate.summary || candidate.metrics || candidate.recommendations
    ));
    if (!sectionSource) return data;
    return {
      ...data,
      sections: sectionSource.sections ?? sectionSource,
    };
  }

  getTarsAdsRows(ads: any): any[] | null {
    if (Array.isArray(ads)) return ads;
    if (!ads || typeof ads !== 'object') return null;

    for (const key of ['ads', 'items', 'rows', 'top_ads', 'top_material_ads', 'by_ad']) {
      if (Array.isArray(ads[key])) return ads[key];
    }
    return null;
  }

  getTarsAdField(ad: any, ...paths: string[]): any {
    for (const path of paths) {
      const value = path.split('.').reduce((current, key) => current?.[key], ad);
      if (value !== null && value !== undefined && value !== '') return value;
    }
    return null;
  }

  formatTarsAdMetric(value: any, format: 'currency' | 'number' | 'percent' | 'share'): string {
    if (value === null || value === undefined || value === '') return 'Unavailable';
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);

    if (format === 'currency') return this.formatTarsMetricValue(numericValue, 'currency');
    if (format === 'number') {
      return numericValue.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }
    return `${(numericValue * 100).toFixed(format === 'share' ? 1 : 2)}%`;
  }

  formatTarsAdClassification(classification: any): string {
    if (!Array.isArray(classification)) return classification ? String(classification) : '';

    const values = new Set(classification);
    if (values.has('insufficient_data')) return 'Insufficient Data';

    const labels: Array<[string, string]> = values.has('high_spend_low_lead')
      ? [
          ['high_spend_low_lead', 'High Spend Low Lead'],
          ['zero_lead_spend_risk', 'Zero Lead Spend Risk'],
        ]
      : [
          ['high_spend', 'High Spend'],
          ['lead_volume_contributor', 'Lead Volume Contributor'],
          ['efficient', 'Efficient'],
          ['short_term_improver', 'Short Term Improver'],
          ['short_term_decliner', 'Short Term Decliner'],
        ];
    return labels.filter(([key]) => values.has(key)).map(([, label]) => label).join(', ');
  }

  getTarsRecommendationMetadata(allOffers: any): any {
    return allOffers?.offer_dependent_context ?? allOffers;
  }

  getTarsGoogleOfferContext(peerBenchmarks: any): any | null {
    const context = peerBenchmarks?.google_offer_context
      ?? peerBenchmarks?.offer_context
      ?? peerBenchmarks;
    return Array.isArray(context?.all_offer_candidates)
      || Array.isArray(context?.same_offer_candidates)
      ? context
      : null;
  }

  getTarsGoogleOfferCandidates(context: any, scope: 'same' | 'all'): any[] {
    const candidates = scope === 'same' ? context?.same_offer_candidates : context?.all_offer_candidates;
    return Array.isArray(candidates) ? candidates : [];
  }

  formatTarsGoogleOfferMetric(value: any, metric: string): string {
    if (value === null || value === undefined || value === '') return '—';
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '—';
    if (['cpl', 'cpc', 'spend'].includes(metric)) return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (['ctr', 'lsr'].includes(metric)) return `${(numericValue * 100).toFixed(2)}%`;
    return numericValue.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  getTarsGoogleBestCampaigns(candidate: any, kpi: string): any[] {
    const bestByKpi = candidate?.best_campaigns_by_kpi;
    const campaigns = bestByKpi && typeof bestByKpi === 'object' ? bestByKpi[kpi] : null;
    return Array.isArray(campaigns) ? campaigns : [];
  }

  getTarsGoogleBestCampaignMetric(candidate: any, kpi: string): any {
    return candidate?.metrics && typeof candidate.metrics === 'object' ? candidate.metrics[kpi] : null;
  }

  hasTarsOfferSpecificContent(specificOffer: any): boolean {
    return [
      specificOffer?.lead_form_candidates,
      specificOffer?.copy_candidates,
      specificOffer?.creative_candidates,
    ].some(candidates => Array.isArray(candidates) && candidates.length > 0);
  }

  getTarsOfferName(peerBenchmarks: any): string {
    if (peerBenchmarks?.offer) return String(peerBenchmarks.offer);
    const offers = Array.isArray(peerBenchmarks?.offers) ? peerBenchmarks.offers : [];
    if (peerBenchmarks?.offer_ambiguous || offers.length > 1) {
      return offers.length ? `Multiple: ${offers.join(', ')}` : 'Multiple';
    }
    return offers.length === 1 ? String(offers[0]) : '—';
  }

  getTarsSpecificOfferTitle(peerBenchmarks: any): string {
    const offerName = this.getTarsOfferName(peerBenchmarks);
    return offerName === '—' ? 'Specific Offer' : `Offer: ${offerName}`;
  }

  formatTarsRecommendationWindow(window: string | null | undefined): string {
    return window ? String(window).replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()) : '—';
  }

  formatTarsRecommendationDomains(peerBenchmarks: any): string {
    const domains = peerBenchmarks?.domains_requested
      ?? peerBenchmarks?.peer_benchmark_request?.domains
      ?? [];
    if (!Array.isArray(domains) || domains.length === 0) return '—';
    return domains.map((domain: string) => {
      if (domain === 'lead_form') return 'Lead Form';
      if (domain === 'creative_copy') return 'Creative/Copy';
      return String(domain).replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
    }).join(', ');
  }

  getTarsCandidateMetric(candidate: any, metricType: 'campaign_median' | 'weighted', metricKey: string): any {
    return candidate?.[`${metricType}_${metricKey}`];
  }

  formatTarsKpiFocus(kpiFocus: unknown): string {
    return Array.isArray(kpiFocus) ? kpiFocus.map(kpi => String(kpi).toUpperCase()).join(', ') : '—';
  }

  getTarsCandidateCpl(candidate: any): number | null {
    const spend = Number(candidate?.total_spend);
    const leads = Number(candidate?.total_leads);
    return Number.isFinite(spend) && Number.isFinite(leads) && leads > 0 ? spend / leads : null;
  }

  formatTarsMetricValue(value: any, format: 'currency' | 'number' | 'percent'): string {
    if (value === null || value === undefined || value === '') return '—';
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '—';

    if (format === 'currency') {
      return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (format === 'percent') {
      return `${(numericValue * 100).toFixed(2)}%`;
    }
    return numericValue.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  getTarsMetricValue(metrics: any, periodKey: string, metricKey: string): any {
    const period = metrics?.[periodKey];
    return period ? period[metricKey] : null;
  }

  getTarsMetricAverageLabel(
    metrics: any,
    periodKey: string,
    averageType: 'brand' | 'stage',
    metricKey: string,
    format: 'currency' | 'number' | 'percent',
    label: string
  ): string | null {
    const value = metrics?.[periodKey]?.benchmarks?.[averageType]?.[metricKey];
    if (value === null || value === undefined || value === '') return null;
    return `${label}: ${this.formatTarsMetricValue(value, format)}`;
  }

  getTarsStageAverageLabel(response: any): string {
    const stage = response?.campaign?.campaign_naming?.stage_label
      ?? response?.campaign?.campaign_naming?.stage
      ?? response?.campaign?.stage;
    return stage ? String(stage).replace(/_/g, ' ') : 'Stage';
  }

  getTarsMetricHealth(metrics: any, periodKey: string, comparisonGroup: 'short_term' | 'long_term', metricKey: string): 'good' | 'bad' | null {
    if (!['ctr', 'lsr', 'cpl'].includes(metricKey)) return null;

    const rawMetricValue = metrics?.[periodKey]?.[metricKey];
    if (rawMetricValue === null || rawMetricValue === undefined || rawMetricValue === '') return null;
    const metricValue = Number(rawMetricValue);
    const threshold = metrics?.comparisons?.[comparisonGroup]?.[metricKey]?.threshold;
    if (!Number.isFinite(metricValue)) return null;

    if (metricKey === 'cpl') {
      if (threshold?.healthy_max === null || threshold?.healthy_max === undefined) return null;
      const healthyMaximum = Number(threshold.healthy_max);
      if (!Number.isFinite(healthyMaximum)) return null;
      return metricValue <= healthyMaximum ? 'good' : 'bad';
    }

    if (threshold?.healthy_min === null || threshold?.healthy_min === undefined) return null;
    const healthyMinimum = Number(threshold.healthy_min);
    if (!Number.isFinite(healthyMinimum)) return null;
    return metricValue >= healthyMinimum ? 'good' : 'bad';
  }

  copyTarsInsightsToCheck(insights: unknown): void {
    const insightsText = Array.isArray(insights)
      ? insights.map(insight => String(insight)).join('\n')
      : String(insights ?? '');
    const insightsControl = this.addCampaignCheckForm.get('insightText');

    if (!insightsText.trim() || !insightsControl) return;

    insightsControl.setValue(insightsText);
    insightsControl.markAsDirty();
    insightsControl.markAsTouched();
  }

  copyTarsMetricsToCheck(metrics: any): void {
    if (!metrics || typeof metrics !== 'object') return;

    const leads = this.reportPurchasesEnabled
      ? metrics.conversions ?? metrics.purchases ?? metrics.leads
      : metrics.leads;
    const costPerLead = this.reportPurchasesEnabled
      ? metrics.cpa ?? metrics.cpl
      : metrics.cpl;
    const conversionRate = this.reportPurchasesEnabled
      ? metrics.conversion_rate ?? metrics.lpcr ?? metrics.lsr
      : metrics.lsr;
    const cpmOrCpc = this.isGooglePlatform ? metrics.cpc : metrics.cpm;
    const fields: Array<[string, any, number?]> = [
      ['leads', leads],
      ['cpl', costPerLead, 2],
      ['ctr', this.toTarsPercentagePoints(metrics.ctr), 2],
      ['impression', metrics.impressions],
      ['cPMOrCPC', cpmOrCpc, 2],
      ['lFSROrLPCR', this.toTarsPercentagePoints(conversionRate), 2],
    ];

    for (const [controlName, rawValue, decimals] of fields) {
      if (rawValue === null || rawValue === undefined || rawValue === '') continue;
      const value = Number(rawValue);
      if (!Number.isFinite(value)) continue;

      const control = this.addCampaignCheckForm.get(controlName);
      if (!control) continue;
      control.setValue(typeof decimals === 'number' ? value.toFixed(decimals) : value);
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  private toTarsPercentagePoints(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue * 100 : null;
  }

  get hasTarsErrors(): boolean {
    return !!this.tarsError || this.tarsResults.some(result => !!result.error);
  }

  async loadTarsReview(retry = false): Promise<void> {
    if (this.tarsLoading || (this.tarsLoaded && !retry)) return;

    const authToken = getAuthToken();
    if (!authToken) {
      this.tarsError = 'The logged-in user auth token is not available.';
      console.error('Unable to load TARS campaign review:', this.tarsError);
      this.tarsLoaded = true;
      return;
    }

    this.tarsLoading = true;
    this.tarsError = null;
    this.tarsResults = [];
    this.activeTarsResultIndex = 0;

    try {
      const reachCampaignId = String(this.campaignData?.id || this.iCampaignId || '').trim();
      if (!reachCampaignId) {
        throw new Error('The REACH campaign ID is not available.');
      }

      const instanceKey = this.instanceService.getKey();
      if (instanceKey !== 'ffm' && instanceKey !== 'prspr') {
        throw new Error(`TARS campaign review is not configured for the ${instanceKey.toUpperCase()} instance.`);
      }
      const reachInstance = instanceKey.toUpperCase();
      const initialResponse = await this.requestTarsReview(authToken, {
        reach_instance: reachInstance,
        reach_campaign_id: reachCampaignId,
        include_google_ads_enrichment: 'false',
      });

      if (!this.isTarsCampaignIdentityError(initialResponse)) {
        this.tarsResults = [{
          campaignId: reachCampaignId,
          campaignName: String(initialResponse?.campaign?.name ?? initialResponse?.campaign?.campaign_name ?? this.campaignData?.name ?? reachCampaignId),
          response: initialResponse,
          error: null,
        }];
        return;
      }

      const identityError = String(initialResponse?.error?.message ?? initialResponse?.error?.code ?? 'TARS campaign identity could not be resolved.');
      console.error('TARS campaign lookup failed; retrying with platform account/campaign IDs:', identityError);

      const metricCampaigns = this.externalCampaignRows.filter(row =>
        [row.impressions, row.clicks, row.leads, row.cost].some(value => Number(value) > 0)
      );
      const adAccounts = Array.isArray(this.campaignData?.campaignAdAccounts)
        ? this.campaignData.campaignAdAccounts
        : [];
      const uniqueAdCampaignIds = Array.from(new Set(
        adAccounts
          .map(adAccount => String(adAccount?.platformCampaignID ?? '').trim())
          .filter(Boolean)
      ));
      const campaignIds = metricCampaigns.length > 1
        ? metricCampaigns.map(row => ({ campaignId: row.campaignId, campaignName: row.campaignName }))
        : [{
            campaignId: String(
              metricCampaigns[0]?.campaignId
              || this.campaignData?.platformCampaignId
              || (uniqueAdCampaignIds.length === 1 ? uniqueAdCampaignIds[0] : '')
            ).trim(),
            campaignName: String(this.campaignData?.name || metricCampaigns[0]?.campaignName || ''),
          }];

      if (campaignIds.some(campaign => !campaign.campaignId)) {
        throw new Error('A platform campaign ID is not available for the TARS fallback request.');
      }

      const uniqueAdAccountIds = Array.from(new Set(
        adAccounts
          .map(adAccount => String(adAccount?.platformAccountID ?? '').replace(/[\s-]/g, ''))
          .filter(Boolean)
      ));
      const primaryPlatformAccountId = String(this.campaignData?.platformAccountId ?? '').replace(/[\s-]/g, '');
      const primaryPlatformCampaignId = String(this.campaignData?.platformCampaignId ?? '').trim();

      this.tarsResults = await Promise.all(campaignIds.map(async (campaign): Promise<TarsReviewResult> => {
        const matchingAdAccount = adAccounts.find(adAccount =>
          String(adAccount?.platformCampaignID ?? '').trim() === campaign.campaignId
        );
        const platformAccountId = String(
          matchingAdAccount?.platformAccountID
          || (campaign.campaignId === primaryPlatformCampaignId ? primaryPlatformAccountId : '')
          || (uniqueAdAccountIds.length === 1 ? uniqueAdAccountIds[0] : '')
        ).replace(/[\s-]/g, '');

        if (!platformAccountId) {
          const message = `The platform account ID is not available for campaign ${campaign.campaignId}.`;
          console.error('Unable to make TARS fallback request:', message);
          return { ...campaign, response: null, error: message };
        }

        try {
          const response = await this.requestTarsReview(authToken, {
            platform_account_id: platformAccountId,
            platform_campaign_id: campaign.campaignId,
            include_google_ads_enrichment: 'false',
          });
          return {
            campaignId: campaign.campaignId,
            campaignName: String(response?.campaign?.name ?? response?.campaign?.campaign_name ?? campaign.campaignName ?? campaign.campaignId),
            response,
            error: null,
          };
        } catch (error: any) {
          if (error?.name === 'AbortError') throw error;

          const message = error?.message ?? 'Unable to load TARS campaign review.';
          console.error(`Error fetching TARS campaign review for ${campaign.campaignId}:`, error);
          return { ...campaign, response: null, error: message };
        }
      }));
    } catch (error: any) {
      if (error?.name === 'AbortError') return;

      this.tarsError = error?.message ?? 'Unable to load TARS campaign review.';
      console.error('Error fetching TARS campaign review:', error);
    } finally {
      this.tarsLoading = false;
      this.tarsLoaded = true;
    }
  }

  private async requestTarsReview(authToken: string, body: Record<string, string>): Promise<any> {
    const response = await fetch('https://dms.reach.marketing/tars/campaign-review-api.php', {
      method: 'POST',
      signal: this.tarsAbortController.signal,
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    const rawBody = await response.text();
    let data: any;
    try {
      data = rawBody.trim() ? JSON.parse(rawBody) : null;
    } catch {
      console.error('TARS returned a non-JSON response:', {
        status: response.status,
        statusText: response.statusText,
        body: rawBody,
      });
      throw new Error(`TARS returned invalid JSON (HTTP ${response.status}).`);
    }

    if (this.isTarsCampaignIdentityError(data)) {
      return data;
    }
    if (!response.ok) {
      console.error('TARS API request failed:', {
        status: response.status,
        statusText: response.statusText,
        response: data,
      });
      const message = data?.error?.message ?? `TARS request failed (HTTP ${response.status}).`;
      throw new Error(`${message} (HTTP ${response.status})`);
    }
    if (data?.success === false) {
      throw new Error(data?.error?.message ?? 'TARS campaign review failed.');
    }
    if (data === null || typeof data !== 'object') {
      throw new Error('TARS returned an empty or invalid response.');
    }
    return this.normalizeTarsResponse(data);
  }

  private isTarsCampaignIdentityError(data: any): boolean {
    const code = String(data?.error?.code ?? '').toLowerCase();
    const message = String(data?.error?.message ?? '').toUpperCase();
    return code === 'campaign_identity_ambiguous'
      || code === 'campaign_not_found'
      || message.includes('CAMPAIGN_IDENTITY_AMBIGUOUS')
      || message.includes('CAMPAIGN_NOT_FOUND');
  }

  private getDefaultExternalWindowUtc(): { startDate: string; endDate: string } {
    // Anchor external windows to local "yesterday" so MTD/reporting does not use GMT boundaries.
    const endDate = moment.utc().local().subtract(1, 'day').format('YYYY-MM-DD');
    const startDate = moment.utc().local().subtract(7, 'days').format('YYYY-MM-DD');
    return { startDate, endDate };
  }

  private ensureIsGooglePlatformInitialized(): boolean {
    if (this.isGooglePlatform !== null) return this.isGooglePlatform;
    const platformRaw = (this.campaignData as any)?.platform;
    const platform = (platformRaw ?? '').toString().toLowerCase();
    const isGoogle = platform.includes('google');
    this.isGooglePlatform = isGoogle;
    return isGoogle;
  }

  private extractTierName(tagName: string): string {
    if (!tagName) return '';

    const partsPipe = tagName.split('|').map(p => p.trim()).filter(Boolean);
    if (partsPipe.length >= 2) {
      return partsPipe[1];
    }

    const lower = tagName.toLowerCase();
    const idx = lower.indexOf('tiered pricing');
    if (idx >= 0) {
      const after = tagName.slice(idx + 'tiered pricing'.length).replace(/^\s*[-:]\s*/, '').trim();
      return after || tagName;
    }

    return tagName;
  }

  private findTierTag(tags: Array<ITag>): ITag | undefined {
    return tags.find(t => {
      const name = (t?.name ?? '').toLowerCase();
      const categories = (t?.categories ?? '').toLowerCase();
      return name.includes('tiered pricing') || (categories === 'pricing' && name.includes('tier'));
    });
  }

  private loadTierBanner(campaignId: string | null | undefined, clientId: string | null | undefined): void {
    if (!campaignId && !clientId) {
      this.campaignTags = [];
      this.clientTags = [];
      this.isTiered = false;
      this.tierName = '';
      return;
    }

    const campaignTags$ = campaignId
      ? this.tagService.getAssociatedTags(campaignId, 1).pipe(
          map((response: any) => Array.isArray(response?.data) ? (response.data as Array<ITag>) : []),
          catchError(() => of([] as Array<ITag>))
        )
      : of([] as Array<ITag>);

    const clientTags$ = clientId
      ? this.tagService.getAssociatedTags(clientId, 2).pipe(
          map((response: any) => Array.isArray(response?.data) ? (response.data as Array<ITag>) : []),
          catchError(() => of([] as Array<ITag>))
        )
      : of([] as Array<ITag>);

    forkJoin({ campaignTags: campaignTags$, clientTags: clientTags$ })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ campaignTags, clientTags }) => {
        this.campaignTags = campaignTags;
        this.clientTags = clientTags;

        const tierTag = this.findTierTag(campaignTags) ?? this.findTierTag(clientTags);
        this.isTiered = !!tierTag;
        this.tierName = tierTag ? this.extractTierName(tierTag.name) : '';
      });
  }

  private isTruthy(value: any): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private async getCampaignBrandName(): Promise<string | null> {
    const campaign = this.campaignData as any;
    const clientId = campaign?.client?.id ?? this.iClientId;
    if (!clientId) return null;

    try {
      const response: any = await new Promise((resolve, reject) => {
        this.clientService.getClientDetails(clientId)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: resolve,
            error: reject
          });
      });

      const brandName = response?.data?.brand ?? null;
      const normalized = String(brandName ?? '').trim();
      return normalized ? normalized : null;
    } catch {
      return null;
    }
  }

  private extractReportPurchasesFlag(payload: any): boolean {
    const isTruthy = (value: any): boolean => this.isTruthy(value);

    if (Array.isArray(payload)) {
      if (isTruthy((payload as any).reportPurchases ?? (payload as any).report_purchases)) return true;
      return payload.some(item => {
        if (!item || typeof item !== 'object') return false;
        return isTruthy((item as any).reportPurchases ?? (item as any).report_purchases);
      });
    }

    if (payload && typeof payload === 'object') {
      return isTruthy((payload as any).reportPurchases ?? (payload as any).report_purchases);
    }

    return false;
  }

  loadDetails() {
    if (!this.iCampaignId) return;
    this.campaignsApiService.getCampaignDetails(this.iCampaignId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        // console.log('Campaign Details Response:', response);
        this.campaignData = response.data;
        this.ensureIsGooglePlatformInitialized();

        // Tier banner prefers campaign tags, then falls back to client tags.
        this.loadTierBanner(this.campaignData?.id ?? this.iCampaignId, this.campaignData?.clientId ?? this.iClientId);

        // Populate hidden identity fields for backend consumers (not shown in UI).
        this.patchHiddenIdentityFields();

        // Ensure lead filtering has a window even if external fetch hasn't completed yet.
        const { startDate, endDate } = this.getDefaultExternalWindowUtc();
        this.DateStart = startDate;
        this.DateStop = endDate;

        this.getLeads();
        void this.loadExternalMetrics();

        // Default next-check assignee to the campaign DMS (backend will assign DMS by default anyway)
        if (!this.addCampaignCheckForm.controls['nextTaskAssignedUserId']?.value && (this.campaignData as any)?.dmsId) {
          this.addCampaignCheckForm.patchValue({ nextTaskAssignedUserId: (this.campaignData as any).dmsId }, { emitEvent: false });
        }

        const campaignId = this.campaignData.id;
        if (campaignId) {
          this.dataFeedService.getNotes(campaignId, NOTES_TYPE.Campaigns)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((response: any) => {
              this.campaignNotes = Array.isArray(response?.data) ? response.data : [];
              this.evaluateCampaignAuditTabDefault();
            });
        } else {
          this.campaignNotes = [];
          this.evaluateCampaignAuditTabDefault();
        }

        this.clientNotes = [];
        this.clientNotesLoaded = false;
        this.tasksDataNotCompleted = [];
        this.upcomingTasksLoaded = false;

        if (this.campaignData.subscriptionActive) {
          this.loadMostRecentOrder();
        } else {
          this.activeSubscription.status = 'NO_ACTIVE_SUBSCRIPTION';
        }
      });
  }

  private loadClientNotes(): void {
    const clientId = this.campaignData?.clientId;
    if (!clientId) {
      this.clientNotesLoaded = true;
      this.clientNotes = [];
      return;
    }

    this.dataFeedService.getNotes(clientId, NOTES_TYPE.Clients)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          const notes = Array.isArray(response?.data) ? response.data : [];
          this.clientNotes = notes.filter((note: INotes) => note.campaignId == null);
          this.clientNotesLoaded = true;
        },
        error: () => {
          this.clientNotes = [];
          this.clientNotesLoaded = true;
        }
      });
  }

  private loadUpcomingTasks(): void {
    if (!this.iCampaignId) {
      this.upcomingTasksLoaded = true;
      this.tasksDataNotCompleted = [];
      return;
    }

    this.taskApiService.getTasksByCampaign(this.iCampaignId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          const tasks = Array.isArray(response?.data) ? response.data : [];
          this.tasksDataNotCompleted = tasks.filter((task: ITask) => task.isCompleted === null || task.isCompleted === false);
          this.upcomingTasksLoaded = true;
        },
        error: () => {
          this.tasksDataNotCompleted = [];
          this.upcomingTasksLoaded = true;
        }
      });
  }

  private patchTaskFieldsFromTrigger(): void {
    if (!this.iTask) return;

    const dueDate = this.iTask.dueDate ? new Date(this.iTask.dueDate) : null;
    const assignedUserId = this.iTask.assignedUserId ?? null;

    // Patch without emitting value changes (keeps this isolated from any other listeners)
    this.addCampaignCheckForm.patchValue(
      {
        dueDate,
        assignedUserId,
        taskIdToComplete: (this.iTask as any)?.id ?? null,
      },
      { emitEvent: false }
    );

    // Keep hidden name fields in sync with the triggering task.
    this.patchHiddenIdentityFields();
  }

  async updateTriggerTask(): Promise<void> {
    if (!this.iTask?.id) {
      this.taskUpdateError = 'No task context available to update.';
      return;
    }

    this.taskUpdateSuccess = false;
    this.taskUpdateError = null;

    const dueDateValue = this.addCampaignCheckForm.controls['dueDate']?.value;
    const assignedUserId = this.addCampaignCheckForm.controls['assignedUserId']?.value;

    if (!dueDateValue && !assignedUserId) {
      this.taskUpdateError = 'Select a due date and/or assigned user first.';
      return;
    }

    const dueDate = dueDateValue ? Utility.getTaskDueDateFromDateTime(dueDateValue) : null;
    const assignedUser = assignedUserId ? this.allUsers.find(u => u.id === assignedUserId) : null;
    const assignedUserName = assignedUser
      ? (((assignedUser as any)?.fullName as string | undefined)
        ?? `${(assignedUser as any)?.firstName ?? ''} ${(assignedUser as any)?.lastName ?? ''}`.trim())
      : '';

    this.taskUpdateSaving = true;
    try {
      // Load the full task so the API receives all fields it expects
      const task: ITask = await new Promise<ITask>((resolve, reject) => {
        this.taskApiService.getTaskDetails(this.iTask!.id)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: (response: any) => {
              const data = response?.data ?? response;
              if (!data) {
                reject(new Error('Task details not found.'));
                return;
              }
              resolve(data as ITask);
            },
            error: (err: any) => reject(err)
          });
      });

      const updated: any = { ...task };
      if (dueDate) updated.dueDate = dueDate;
      if (assignedUserId) {
        updated.assignedUserId = assignedUserId;
        if (assignedUserName) updated.assignedUserName = assignedUserName;
      }

      await new Promise<void>((resolve, reject) => {
        this.taskApiService.saveTask(updated)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: () => resolve(),
            error: (err: any) => reject(err)
          });
      });

      // Update local task context so reopening doesn't revert
      this.iTask = {
        ...this.iTask,
        dueDate: dueDate ? dueDate : this.iTask.dueDate,
        assignedUserId: assignedUserId ? assignedUserId : this.iTask.assignedUserId,
        assignedUserName: assignedUserName ? assignedUserName : this.iTask.assignedUserName,
      };

      this.taskUpdateSuccess = true;
      this.oTaskUpdated.emit();
      this.activeModal.close('task-updated');
    } catch (e: any) {
      this.taskUpdateError = e?.message ?? 'Failed to update task.';
    } finally {
      this.taskUpdateSaving = false;
    }
  }

  externalMetrics: any = null;
  externalMetricsLoading = false;
  externalMetricsError: string | null = null;

  externalCampaignRows: Array<{
    campaignId: string;
    campaignName: string;
    impressions: number;
    clicks: number;
    leads: number;
    cost: number;
    ctrPct: number | null;
    cpmOrCpc: number | null;
    lsrPct: number | null;
    cpl: number | null;
  }> = [];
  hasExternalCampaignBreakdown = false;

  externalDailyRows: Array<{
    date: string;
    impressions: number;
    clicks: number;
    leads: number;
    cost: number;
    reachLeads: number;
  }> = [];

  externalRangeComparisons: Array<{
    label: string;
    startDate: string;
    endDate: string;
    impressions: number;
    clicks: number;
    leads: number;
    conversionActions?: number;
    cost: number;
    cpl: number | null;
    cpa?: number | null;
    ctrPct: number | null;
    cpm: number | null;
    lsrPct: number | null;
    convRatePct?: number | null;
  }> = [];

  externalMonthToDateCost: number | null = null;

  get adSpendRemaining(): number {
    const mtd = this.externalMonthToDateCost;
    if (mtd === null || mtd === undefined) return 0;
    const remaining = this.thisMonthsAdspend - mtd;
    return Number.isFinite(remaining) ? remaining : 0;
  }

  get dailySpendShouldBe(): number {
    const days = this.daysLeftInMonthIncludingToday;
    if (!days || days <= 0) return 0;
    const perDay = this.adSpendRemaining / days;
    if (!Number.isFinite(perDay)) return 0;
    return Math.max(0, Math.round(perDay));
  }

  get projectedMonthlySpend(): number {
    const mtd = this.externalMonthToDateCost;
    if (mtd === null || mtd === undefined) return 0;

    const yesterdayLocal = moment.utc().local().subtract(1, 'day');
    const elapsedDays = yesterdayLocal.date();
    const totalDaysInMonth = yesterdayLocal.daysInMonth();

    if (!elapsedDays || elapsedDays <= 0 || !totalDaysInMonth || totalDaysInMonth <= 0) return 0;

    const projected = (mtd / elapsedDays) * totalDaysInMonth;
    return Number.isFinite(projected) ? projected : 0;
  }

  get projectedMonthlySpendDelta(): number {
    const delta = this.projectedMonthlySpend - this.thisMonthsAdspend;
    return Number.isFinite(delta) ? Math.abs(delta) : 0;
  }

  get projectedMonthlySpendDirection(): 'over' | 'under' | 'matching' {
    const delta = this.projectedMonthlySpend - this.thisMonthsAdspend;
    const paceTolerance = this.thisMonthsAdspend * 0.03;
    if (!Number.isFinite(delta) || Math.abs(delta) < paceTolerance) return 'matching';
    return delta > 0 ? 'over' : 'under';
  }

  get projectedMonthlySpendDisplayDirection(): 'over' | 'under' {
    const delta = this.projectedMonthlySpend - this.thisMonthsAdspend;
    if (!Number.isFinite(delta)) return 'under';
    return delta > 0 ? 'over' : 'under';
  }

  get projectedMonthlySpendDisplayDirectionKey(): 'OVER' | 'UNDER' {
    return this.projectedMonthlySpendDisplayDirection === 'over' ? 'OVER' : 'UNDER';
  }

  externalDailyLoading = false;
  externalComparisonsLoading = false;

  private async readExternalMetricsResponse(response: Response): Promise<any | null> {
    const body = await response.text();
    if (!body.trim()) {
      if (!response.ok) throw new Error(`TARS metrics request failed (HTTP ${response.status}).`);
      return null;
    }

    let data: any;
    try {
      data = JSON.parse(body);
    } catch {
      throw new Error(`TARS metrics returned invalid JSON (HTTP ${response.status}).`);
    }
    if (!response.ok) throw new Error(data?.error?.message ?? `TARS metrics request failed (HTTP ${response.status}).`);
    return this.normalizeTarsMetricsPayload(data);
  }

  private normalizeTarsMetricsPayload(payload: any): any {
    if (!payload?.success || !Array.isArray(payload.campaigns)) return payload;

    // Keep the existing extraction/rendering code compatible while the new
    // TARS endpoint returns the cleaner structured campaign envelope.
    const normalized = payload.campaigns.map((campaign: any) => {
      const target = campaign?.target ?? {};
      const totals = { ...(campaign?.totals ?? {}) };
      const campaignId = String(totals.campaign_id ?? target.platform_campaign_id ?? '').trim();
      totals.campaign_id = campaignId;
      totals.account_id ??= target.platform_account_id;
      totals.platform ??= target.platform;
      totals.campaign_name ??= target.campaign_name;
      const legacyCampaign: any = { totals };
      for (const [key, value] of Object.entries(campaign ?? {})) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(key)) legacyCampaign[key] = value;
      }
      return { [campaignId]: legacyCampaign };
    });
    if (payload.type === 'campaign_check_dashboard') {
      return {
        ...payload,
        campaigns: normalized,
      };
    }

    // Preserve endpoint-level metadata for the existing extraction helpers.
    (normalized as any).reportPurchases = payload.reportPurchases ?? payload.report_purchases;
    return normalized;
  }

  private async requestTarsMetrics(
    reachCampaignId: string,
    platform: string,
    startDate: string,
    endDate: string,
    daily = false,
    extraBody: Record<string, unknown> = {},
  ): Promise<any | null> {
    const authToken = getAuthToken();
    if (!authToken) throw new Error('The logged-in user auth token is not available.');

    const response = await fetch('https://dms.reach.marketing/tars/reach-pull-api.php', {
      method: 'POST',
      signal: this.tarsAbortController.signal,
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        reach_campaign_id: reachCampaignId,
        platform,
        start_date: startDate,
        end_date: endDate,
        daily,
        ...extraBody,
      }),
    });
    return this.readExternalMetricsResponse(response);
  }

  private buildExternalComparisonRow(comparison: any): {
    label: string;
    startDate: string;
    endDate: string;
    impressions: number;
    clicks: number;
    leads: number;
    conversionActions: number;
    cost: number;
    cpl: number | null;
    cpa: number | null;
    ctrPct: number | null;
    cpm: number | null;
    lsrPct: number | null;
    convRatePct: number | null;
  } {
    const totals = comparison?.totals ?? {};
    const impressions = Number(totals.impressions ?? 0);
    const clicks = Number(totals.clicks ?? 0);
    const leads = Number(totals.leads ?? 0);
    const conversionActions = Number(totals.conversion_actions ?? 0);
    const cost = Number(totals.cost ?? 0);
    const primaryMetricCount = this.reportPurchasesEnabled ? conversionActions : leads;

    return {
      label: String(comparison?.key ?? ''),
      startDate: String(comparison?.start_date ?? ''),
      endDate: String(comparison?.end_date ?? ''),
      impressions,
      clicks,
      leads,
      conversionActions,
      cost,
      cpl: primaryMetricCount > 0 ? cost / primaryMetricCount : null,
      cpa: conversionActions > 0 ? cost / conversionActions : null,
      ctrPct: impressions > 0 ? (clicks / impressions) * 100 : null,
      cpm: impressions > 0 ? cost / (impressions / 1000) : null,
      lsrPct: clicks > 0 ? (leads / clicks) * 100 : null,
      convRatePct: clicks > 0 ? (conversionActions / clicks) * 100 : null,
    };
  }

  private async loadExternalMetrics(): Promise<void> {
    if (!this.iCampaignId) return;

    const isGoogle = this.ensureIsGooglePlatformInitialized();
    const platform = isGoogle ? 'google' : 'meta';
    const { startDate, endDate } = this.getDefaultExternalWindowUtc();
    const yesterday = moment.utc().local().subtract(1, 'day');
    const comparisonRanges = [
      {
        key: 'LAST_7_DAYS',
        start_date: startDate,
        end_date: endDate,
      },
      {
        key: 'PREVIOUS_7_DAYS',
        start_date: yesterday.clone().subtract(13, 'days').format('YYYY-MM-DD'),
        end_date: yesterday.clone().subtract(7, 'days').format('YYYY-MM-DD'),
      },
      {
        key: 'LAST_30_DAYS',
        start_date: yesterday.clone().subtract(29, 'days').format('YYYY-MM-DD'),
        end_date: endDate,
      },
      {
        key: 'MONTH_TO_DATE',
        start_date: yesterday.clone().startOf('month').format('YYYY-MM-DD'),
        end_date: endDate,
      },
    ];
    const fullStartDate = comparisonRanges
      .map(range => range.start_date)
      .sort()[0];

    this.DateStart = startDate;
    this.DateStop = endDate;

    const reachCampaignId = String(this.iCampaignId).trim();

    try {
      this.externalMetricsLoading = true;
      this.externalDailyLoading = true;
      this.externalComparisonsLoading = true;
      this.externalMetricsError = null;
      this.hasExternalCampaignBreakdown = false;

      const data = await this.requestTarsMetrics(
        reachCampaignId,
        platform,
        fullStartDate,
        endDate,
        true,
        {
          response_mode: 'campaign_check_dashboard',
          daily_start_date: startDate,
          daily_end_date: endDate,
          comparison_ranges: comparisonRanges,
        }
      );

      this.externalMetrics = data;
      this.reportPurchasesEnabled = !!data?.reportPurchases;
      this.conversionActionBreakdown = this.extractConversionActionBreakdown(data?.campaigns);
      const campaignRows = this.extractCampaignRows(data?.campaigns);
      this.externalCampaignRows = campaignRows.length > 1
        ? campaignRows.filter(row => this.hasCampaignActivity(row))
        : campaignRows;
      this.hasExternalCampaignBreakdown = this.externalCampaignRows.length > 1;

      const totals = data?.summary;
      if (!totals) throw new Error('TARS dashboard metrics response did not include a summary.');

      const impressions = Number(totals.impressions ?? 0);
      const clicks = Number(totals.clicks ?? 0);
      const primaryMetricCount = this.reportPurchasesEnabled
        ? Number(totals.conversion_actions ?? 0)
        : Number(totals.leads ?? 0);
      const cost = Number(totals.cost ?? 0);
      const ctrPct = impressions > 0 ? (clicks / impressions) * 100 : null;
      const cpl = primaryMetricCount > 0 ? cost / primaryMetricCount : null;
      const leadRatePct = clicks > 0 ? (primaryMetricCount / clicks) * 100 : null;

      this.patchNumber('impression', impressions);
      this.patchNumber('leads', primaryMetricCount);
      this.patchNumber('ctr', ctrPct, 2);
      this.patchNumber('cpl', cpl, 2);
      this.patchNumber(
        'cPMOrCPC',
        this.isGooglePlatform
          ? (clicks > 0 ? cost / clicks : null)
          : (impressions > 0 ? cost / (impressions / 1000) : null),
        2
      );
      this.patchNumber('lFSROrLPCR', leadRatePct, 2);

      this.externalDailyRows = (Array.isArray(data?.daily) ? data.daily : []).map((row: any) => ({
        date: String(row?.date ?? ''),
        impressions: Number(row?.impressions ?? 0),
        clicks: Number(row?.clicks ?? 0),
        leads: Number(row?.leads ?? 0),
        cost: Number(row?.cost ?? 0),
        reachLeads: 0,
      }));
      this.applyReachLeadsToDailyRows();
      this.externalRangeComparisons = (Array.isArray(data?.comparisons) ? data.comparisons : [])
        .map((comparison: any) => this.buildExternalComparisonRow(comparison));

      const monthToDate = this.externalRangeComparisons.find(row => row.label === 'MONTH_TO_DATE');
      this.externalMonthToDateCost = monthToDate?.cost ?? null;
    } catch (e: any) {
      if (e?.name === 'AbortError') return;

      console.error('Error fetching external campaign metrics:', e);
      this.externalMetricsError = 'Unable to load external metrics.';
      this.externalDailyRows = [];
      this.externalRangeComparisons = [];
      this.externalMonthToDateCost = null;
    } finally {
      this.externalMetricsLoading = false;
      this.externalDailyLoading = false;
      this.externalComparisonsLoading = false;
    }
  }

  private extractDailyRows(payload: any): Array<{
    date: string;
    impressions: number;
    clicks: number;
    leads: number;
    conversionActions?: number;
    cost: number;
    reachLeads: number;
  }> {
    const toNumber = (v: any): number | null => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const normalizeDate = (d: any): string | null => {
      if (!d) return null;
      const s = d.toString();
      // Expect YYYY-MM-DD or ISO; let moment normalize.
      const m = moment.utc(s);
      return m.isValid() ? m.format('YYYY-MM-DD') : null;
    };

    const rowsByDate = new Map<string, { date: string; impressions: number; clicks: number; leads: number; conversionActions?: number; cost: number; reachLeads: number }>();

    const add = (dateRaw: any, metrics: any) => {
      const date = normalizeDate(dateRaw);
      if (!date) return;
      if (!metrics || typeof metrics !== 'object') return;

      const impressions = toNumber(metrics.impressions ?? metrics.impression) ?? 0;
      const clicks = toNumber(metrics.clicks) ?? 0;
      const leads = toNumber(metrics.leads) ?? 0;
      const conversionActions = toNumber(
        metrics.conversion_actions
        ?? metrics.conversionActions
        ?? metrics.conversions
        ?? metrics.purchases
      ) ?? undefined;
      const cost = toNumber(metrics.cost) ?? 0;

      const existing = rowsByDate.get(date);
      if (existing) {
        existing.impressions += impressions;
        existing.clicks += clicks;
        existing.leads += leads;
        if (conversionActions !== undefined) {
          existing.conversionActions = (existing.conversionActions ?? 0) + conversionActions;
        }
        existing.cost += cost;
      } else {
        rowsByDate.set(date, { date, impressions, clicks, leads, conversionActions, cost, reachLeads: 0 });
      }
    };

    // Heuristic parsing to handle multiple possible shapes
    // 1) Array of daily items: [{ date: 'YYYY-MM-DD', totals: {...}}]
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (!item || typeof item !== 'object') continue;

        // If item has explicit date fields
        const date = (item as any).date ?? (item as any).day ?? (item as any).Date;
        if (date) {
          const totals = (item as any).totals ?? (item as any).metrics ?? item;
          add(date, totals);
          continue;
        }

        // If item is keyed by date or campaignId
        for (const [key, value] of Object.entries(item)) {
          const maybeDate = normalizeDate(key);
          if (maybeDate) {
            const totals = (value as any)?.totals ?? value;
            add(maybeDate, totals);
            continue;
          }

          // If value is keyed by date inside campaign object
          if (value && typeof value === 'object') {
            for (const [k2, v2] of Object.entries(value as any)) {
              const d2 = normalizeDate(k2);
              if (!d2) continue;
              const totals2 = (v2 as any)?.totals ?? v2;
              add(d2, totals2);
            }
          }
        }
      }
    } else if (payload && typeof payload === 'object') {
      // 2) Object keyed by date: { 'YYYY-MM-DD': { totals: {...} }, ... }
      for (const [key, value] of Object.entries(payload)) {
        const maybeDate = normalizeDate(key);
        if (maybeDate) {
          const totals = (value as any)?.totals ?? value;
          add(maybeDate, totals);
          continue;
        }

        // 3) Object keyed by campaignId containing dates
        if (value && typeof value === 'object') {
          for (const [k2, v2] of Object.entries(value as any)) {
            const d2 = normalizeDate(k2);
            if (!d2) continue;
            const totals2 = (v2 as any)?.totals ?? v2;
            add(d2, totals2);
          }
        }
      }
    }

    // Fill reachLeads from ReachMe leads data by createdAt day (UTC)
    const reachLeadsByDate = this.buildReachLeadsByDate();
    const rows = Array.from(rowsByDate.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(r => ({
        ...r,
        reachLeads: reachLeadsByDate.get(r.date) ?? 0
      }));

    return rows;
  }

  private buildReachLeadsByDate(): Map<string, number> {
    const counts = new Map<string, number>();
    for (const lead of (this.leadsData ?? [])) {
      const createdAt = (lead as any)?.createdAt;
      if (!createdAt) continue;
      const m = moment.utc(createdAt);
      if (!m.isValid()) continue;
      const key = m.format('YYYY-MM-DD');
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }

  private applyReachLeadsToDailyRows(): void {
    if (!this.externalDailyRows || this.externalDailyRows.length === 0) return;
    const reachLeadsByDate = this.buildReachLeadsByDate();
    this.externalDailyRows = this.externalDailyRows.map(r => ({
      ...r,
      reachLeads: reachLeadsByDate.get(r.date) ?? 0
    }));
  }

  private extractCampaignRows(payload: any): Array<{
    campaignId: string;
    campaignName: string;
    impressions: number;
    clicks: number;
    leads: number;
    conversionActions?: number;
    cost: number;
    ctrPct: number | null;
    cpmOrCpc: number | null;
    lsrPct: number | null;
    cpl: number | null;
    cpa?: number | null;
    convRatePct?: number | null;
  }> {
    const toNumber = (v: any): number | null => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const rowsById = new Map<string, {
      campaignId: string;
      campaignName: string;
      impressions: number;
      clicks: number;
      leads: number;
      conversionActions: number;
      cost: number;
      ctrPct: number | null;
      cpmOrCpc: number | null;
      lsrPct: number | null;
      cpl: number | null;
      cpa: number | null;
      convRatePct: number | null;
    }>();

    const addRow = (campaignId: string, value: any) => {
      if (!campaignId) return;
      if (!value || typeof value !== 'object') return;

      const totals = (value as any).totals ?? value;
      if (!totals || typeof totals !== 'object') return;

      const impressions = toNumber((totals as any).impressions ?? (totals as any).impression) ?? 0;
      const clicks = toNumber((totals as any).clicks) ?? 0;
      const leads = toNumber((totals as any).leads) ?? 0;
      const conversionActions = toNumber(
        (totals as any).conversion_actions
        ?? (totals as any).conversionActions
        ?? (totals as any).conversions
        ?? (totals as any).purchases
      ) ?? 0;
      const cost = toNumber((totals as any).cost) ?? 0;

      const ctrPct = impressions > 0 ? (clicks / impressions) * 100 : null;
      const lsrPct = clicks > 0 ? (leads / clicks) * 100 : null;
      const cpl = this.reportPurchasesEnabled
        ? (conversionActions > 0 ? (cost / conversionActions) : null)
        : (leads > 0 ? (cost / leads) : null);
      const cpa = conversionActions > 0 ? (cost / conversionActions) : null;
      const convRatePct = clicks > 0 ? (conversionActions / clicks) * 100 : null;
      const cpmOrCpc = this.isGooglePlatform
        ? (clicks > 0 ? (cost / clicks) : null)
        : (impressions > 0 ? (cost / (impressions / 1000)) : null);

      const campaignNameRaw = (value as any).campaign_name ?? (value as any).name ?? (totals as any).campaign_name ?? (totals as any).name ?? campaignId;
      const campaignName = (campaignNameRaw ?? campaignId).toString();

      // If any campaign name includes the special DirectBuy marker, enable purchase reporting
      try {
        if (campaignName && campaignName.indexOf('_DirectBuy_') !== -1) {
          this.reportPurchasesEnabled = true;
        }
      } catch (err) {
        // ignore errors here — this is a best-effort toggle
      }

      const existing = rowsById.get(campaignId);
      if (existing) {
        existing.impressions += impressions;
        existing.clicks += clicks;
        existing.leads += leads;
        existing.conversionActions += conversionActions;
        existing.cost += cost;

        // Recompute derived metrics after aggregation
        existing.ctrPct = existing.impressions > 0 ? (existing.clicks / existing.impressions) * 100 : null;
        existing.lsrPct = existing.clicks > 0 ? (existing.leads / existing.clicks) * 100 : null;
        existing.cpl = this.reportPurchasesEnabled
          ? (existing.conversionActions > 0 ? (existing.cost / existing.conversionActions) : null)
          : (existing.leads > 0 ? (existing.cost / existing.leads) : null);
        existing.cpa = existing.conversionActions > 0 ? (existing.cost / existing.conversionActions) : null;
        existing.convRatePct = existing.clicks > 0 ? (existing.conversionActions / existing.clicks) * 100 : null;
        existing.cpmOrCpc = this.isGooglePlatform
          ? (existing.clicks > 0 ? (existing.cost / existing.clicks) : null)
          : (existing.impressions > 0 ? (existing.cost / (existing.impressions / 1000)) : null);

        if ((existing.campaignName === existing.campaignId || !existing.campaignName) && campaignName) {
          existing.campaignName = campaignName;
        }
      } else {
        rowsById.set(campaignId, {
          campaignId,
          campaignName,
          impressions,
          clicks,
          leads,
          conversionActions,
          cost,
          ctrPct,
          cpmOrCpc,
          lsrPct,
          cpl,
          cpa,
          convRatePct,
        });
      }
    };

    // Array payload: [ { "<id>": { totals: {...}, campaignName?: ... } }, ... ]
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (!item || typeof item !== 'object') continue;
        for (const [key, value] of Object.entries(item)) {
          addRow(key, value);
        }
      }
    } else if (payload && typeof payload === 'object') {
      for (const [key, value] of Object.entries(payload)) {
        addRow(key, value);
      }
    }

    return Array.from(rowsById.values()).sort((a, b) => (a.campaignName ?? '').localeCompare(b.campaignName ?? ''));
  }

  private hasCampaignActivity(row: {
    impressions?: number;
    clicks?: number;
    leads?: number;
    conversionActions?: number;
    cost?: number;
  }): boolean {
    return [
      row.impressions,
      row.clicks,
      row.leads,
      row.conversionActions,
      row.cost,
    ].some(value => Number(value ?? 0) > 0);
  }

  private patchNumber(controlName: string, value: number | null | undefined, decimals?: number): void {
    if (value === null || value === undefined) return;
    if (!Number.isFinite(value)) return;
    const v = (typeof decimals === 'number') ? value.toFixed(decimals) : value;
    this.addCampaignCheckForm.controls[controlName]?.patchValue(v);
  }

  private getPrimaryMetricCountFromTotals(totals: { [key: string]: any }): number {
    const toNumber = (v: any): number | null => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const leads = toNumber(totals?.leads);
    const conversionActions = toNumber(
      totals?.conversion_actions
      ?? totals?.conversionActions
      ?? totals?.conversions
      ?? totals?.purchases
    );

    if (this.reportPurchasesEnabled) {
      return conversionActions ?? leads ?? 0;
    }

    return leads ?? conversionActions ?? 0;
  }

  getDisplayMetricCount(row: { leads?: number; conversionActions?: number; purchases?: number } | null | undefined): number {
    const leads = Number(row?.leads ?? 0);
    const conversionActions = Number(row?.conversionActions ?? row?.purchases ?? 0);

    if (this.reportPurchasesEnabled) {
      return Number.isFinite(conversionActions) && conversionActions > 0 ? conversionActions : leads;
    }

    return Number.isFinite(leads) ? leads : 0;
  }

  getDisplayMetricCost(row: { cost?: number; leads?: number; conversionActions?: number; purchases?: number } | null | undefined): number | null {
    const count = this.getDisplayMetricCount(row);
    const cost = Number(row?.cost ?? 0);
    if (!Number.isFinite(cost) || count <= 0) return null;
    return cost / count;
  }

  getDisplayMetricRate(row: { clicks?: number; leads?: number; conversionActions?: number; purchases?: number } | null | undefined): number | null {
    const clicks = Number(row?.clicks ?? 0);
    const count = this.getDisplayMetricCount(row);
    if (!Number.isFinite(clicks) || clicks <= 0 || count < 0) return null;
    return (count / clicks) * 100;
  }

  private extractAndAggregateTotals(payload: any): { impressions: number; clicks: number; leads: number; conversionActions: number; cost: number } | null {
    const aggregate = { impressions: 0, clicks: 0, leads: 0, conversionActions: 0, cost: 0 };
    let foundAny = false;

    const toNumber = (v: any): number | null => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const addTotals = (totals: any) => {
      if (!totals || typeof totals !== 'object') return;
      const impressions = toNumber(totals.impressions ?? totals.impression);
      const clicks = toNumber(totals.clicks);
      const leads = toNumber(totals.leads);
      const conversionActions = toNumber(
        totals.conversion_actions
        ?? totals.conversionActions
        ?? totals.conversions
        ?? totals.purchases
      );
      const cost = toNumber(totals.cost);

      if (impressions !== null) aggregate.impressions += impressions;
      if (clicks !== null) aggregate.clicks += clicks;
      if (leads !== null) aggregate.leads += leads;
      if (conversionActions !== null) aggregate.conversionActions += conversionActions;
      if (cost !== null) aggregate.cost += cost;

      foundAny = true;
    };

    // Array payload: [ { "<id>": { totals: {...} } }, ... ]
    if (Array.isArray(payload)) {
      for (const item of payload) {
        if (!item || typeof item !== 'object') continue;
        for (const value of Object.values(item)) {
          if (!value || typeof value !== 'object') continue;
          const totals = (value as any).totals ?? value;
          addTotals(totals);
        }
      }
    } else if (payload && typeof payload === 'object') {
      // Fallback: single object payload
      const totals = payload.totals ?? payload;
      addTotals(totals);
    }

    return foundAny ? aggregate : null;
  }

  private extractConversionActionBreakdown(payload: any): Array<{ label: string; value: number }> {
    const actionKeys = [
      { key: 'purchases', label: 'Purchases' },
      { key: 'booking', label: 'Booking' },
      { key: 'add_to_cart', label: 'Add to Cart' },
      { key: 'checkout', label: 'Checkout' },
      { key: 'leads', label: 'Leads' },
    ];

    const totals = actionKeys.reduce((acc, action) => {
      acc[action.key] = 0;
      return acc;
    }, {} as Record<string, number>);

    const addValues = (source: any) => {
      if (!source || typeof source !== 'object') return;
      for (const action of actionKeys) {
        const value = Number(source?.[action.key] ?? 0);
        if (Number.isFinite(value) && value !== 0) {
          totals[action.key] += value;
        }
      }
    };

    const visitTotals = (item: any) => {
      if (!item || typeof item !== 'object') return;

      const directTotals = item.totals ?? item;
      addValues(directTotals);

      for (const value of Object.values(item)) {
        if (!value || typeof value !== 'object') continue;
        const nestedTotals = (value as any).totals ?? value;
        addValues(nestedTotals);
      }
    };

    if (Array.isArray(payload)) {
      for (const item of payload) {
        visitTotals(item);
      }
    } else {
      visitTotals(payload);
    }

    return actionKeys
      .map(action => ({ label: action.label, value: totals[action.key] ?? 0 }))
      .filter(item => item.value > 0);
  }

  onSubmit() {
    this.submitted = true;
    if (this.addCampaignCheckForm.invalid)
      return;
    if (this.f.id.value === null)
      this.f.id.setValue(createGuid());

    // Ensure identity fields are populated right before emitting.
    this.patchHiddenIdentityFields();

    var request = { ...this.addCampaignCheckForm.value };

    // Add Campaign Audit selections (labels only) for downstream logic (note/email).
    request.campaignAuditItems = this.getCheckedCampaignAuditItems();
    request.campaignAuditAdditionalInfo = String(this.addCampaignCheckForm?.controls?.['auditAdditionalInfo']?.value ?? '').trim();

    // Add Red Alert Deep Dive selections (labels only) for downstream logic (note/email).
    request.redAlertDeepDiveItems = this.getCheckedRedAlertDeepDiveItems();
    request.reportPurchases = this.reportPurchasesEnabled;

    // Task quick-update fields are handled separately and should not be submitted as part of the campaign check.
    delete request.dueDate;
    delete request.assignedUserId;

    // Don't send raw audit checkbox fields to the backend.
    for (const entry of this.campaignAuditChecklist) {
      delete request[entry.key];
    }
    delete request.auditAdditionalInfo;

    // Don't send raw Red Alert Deep Dive fields to the backend.
    const redAlertRawKeys: string[] = [
      ...this.redAlertDeepDiveChecklist.map(x => x.key),
      'radDdAdditionalInfo',
      'radActFixedConversionTracking',
      'radActRevertedPreviousChanges',
      ...this.redAlertRevertedPreviousChangesChecklist.map(x => x.key),
      'radActRevertedOtherText',
      'radActKpiProblemFoundAndCorrected',
      ...this.redAlertKpiProblemChecklist.map(x => x.key),
      'radActKpiOtherText',
      'radActSentToStrategyForReview',
      'radActAdditionalInfo',
    ];
    for (const key of redAlertRawKeys) {
      delete request[key];
    }

    request = this.trimObjectStrings(request);

    request.campaignId = this.iCampaignId;
    request.clientId = this.iClientId;
    request.leadId = this.iLeadId;
    request.isGooglePlatform = this.isGooglePlatform;
    request.type = this.iType;
    request.addFiles = this.files.filter(x => x.link === undefined).map(x => { return { name: x.name, content: x.content } });
    request.removeFiles = this.removedFiles;
    request.addTaskDate = Utility.getTaskDueDateFromDateTime(request.addTaskDate);

    // If this modal was opened from a task (DMS dashboard), ensure the backend gets a valid GUID here.
    if (!request.taskIdToComplete && (this.iTask as any)?.id) {
      request.taskIdToComplete = (this.iTask as any).id;
    }
    if (typeof request.taskIdToComplete === 'string' && request.taskIdToComplete.trim() === '') {
      request.taskIdToComplete = null;
    }

    this.oSaveClicked.emit(request);
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  ngOnDestroy() {
    this.tarsAbortController.abort();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private buildAdspendProductsFromOrder(orderData: any): any[] {
    const rawItems =
      orderData?.products ??
      orderData?.items ??
      orderData?.orderItems ??
      orderData?.order?.items ??
      [];

    const items: any[] = Array.isArray(rawItems) ? rawItems : [];

    const isGoogle = !!this.isGooglePlatform || (typeof this.campaignData?.platform === 'string' && this.campaignData.platform.toLowerCase().includes('google'));
    const platformKeywords = isGoogle
      ? ['google', 'adwords', 'ppc']
      : ['facebook', 'meta', 'instagram'];

    const looksLikeAdSpend = (item: any): boolean => {
      const name = String(item?.name ?? item?.display ?? '').toLowerCase();

      const adSpendMatch = name.includes('ad spend') || name.includes('adspend');
      if (!adSpendMatch) return false;

      const hasPlatformHint = platformKeywords.some(k => name.includes(k));
      return hasPlatformHint || platformKeywords.length === 0;
    };

    return items
      .filter(looksLikeAdSpend)
      .map((item: any) => {
        const name = item?.name ?? item?.display ?? 'Ad Spend';
        return {
          name,
          raw: item,
        };
      });
  }

  private calculateThisMonthsAdspend(adspendProducts: any[]): number {
    const toNumber = (v: any): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const products = Array.isArray(adspendProducts) ? adspendProducts : [];
    return products.reduce((sum: number, p: any) => {
      const raw = p?.raw ?? p;
      const proratePrice = toNumber(raw?.proratePrice);
      const couponAmount = toNumber(raw?.couponAmount);
      return sum + (proratePrice - couponAmount);
    }, 0);
  }

  files: any[] = [];
  removedFiles: any[] = [];
  onSelect(event: any) {
    for (var i = 0; i < event.addedFiles.length; i++) {
      var file = event.addedFiles[i];
      var that = this.files;
      this.readFile(file).then((fileContents: any) => {
        file.content = fileContents;
        that.push(file);
      })
    }
  }

  onRemove(event: any) {
    this.files.splice(this.files.indexOf(event), 1);
    if (event.id) {
      this.removedFiles.push(event.id);
    }
  }

  readFile(file: File): any {
    return new Promise<string | ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        var result: any = (e.target as FileReader).result;
        return resolve(result);
      };
      reader.onerror = e => {
        console.error(`FileReader failed on file ${file.name}.`);
        return reject(null);
      };
      if (!file) {
        console.error('No file to read.');
        return reject(null);
      }
      reader.readAsDataURL(file);
    });
  }

  loadMostRecentOrder(): void {
    this.adspendProduct = [];
    this.thisMonthsAdspend = 0;
    this.subscriptionService.getAllSubscriptions(this.campaignData.id, this.campaignData.clientId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.subscriptions) {
          const subscriptions = response.subscriptions.map((x: any) => {
            x.dms = x.campaigns[0].dms;
            x.salesrep = x.campaigns[0].salesRep;
            return x;
          });
          // console.log('Subscriptions Response:', subscriptions);
          const activeSubscription: any = subscriptions.find((sub: any) => sub.status === 'Active');
          // console.log('Active Subscription:', activeSubscription);
          this.activeSubscription = activeSubscription;
          if (activeSubscription) {
            this.subscriptionService.getSubscription(activeSubscription.id)
              .pipe(takeUntil(this.destroyed$))
              .subscribe(
                (response: any) => {
                  if (response && response.subscription.orders) {
                    const sortedOrders: Array<{ id?: string; generatedOn: string; paid: boolean; isActive: boolean; refundText: string; status?: string }> = response.subscription.orders.sort((a: { generatedOn: string }, b: { generatedOn: string }) => new Date(b.generatedOn).getTime() - new Date(a.generatedOn).getTime());
                    // console.log('Sorted Orders:', sortedOrders);

                    this.mostRecentOrder = sortedOrders[0];
                    this.mostRecentOrder.status = this.getOrderStatusKey(this.mostRecentOrder);
                    // console.log('Most Recent Order:', this.mostRecentOrder);
                    const mostRecentOrderId = this.mostRecentOrder.id;
                    const currentMonthOrders = sortedOrders
                      .filter((o: any) => o?.paid === true)
                      .filter((o: any) => {
                        if (!o?.generatedOn) return false;
                        const m = moment.utc(o.generatedOn).local();
                        return m.isValid() && m.isSame(moment.utc().local(), 'month');
                      })
                      .filter((o: any) => !!o?.id);

                    if (currentMonthOrders.length === 0) {
                      this.orderData = {};
                      this.adspendProduct = [];
                      this.thisMonthsAdspend = 0;
                      return;
                    }

                    const detailCalls = currentMonthOrders.map((o: any) =>
                      this.orderApiService.getShopOrderDetails(o.id)
                        .pipe(
                          map((detailResponse: any) => {
                            // console.log('Order Details Response for Order ID', o.id, ':', detailResponse);
                            const data = detailResponse?.data;
                            return {
                              orderId: o.id,
                              data,
                              adspendProducts: this.buildAdspendProductsFromOrder(data),
                            };
                          }),
                          catchError((err: any) => {
                            console.error('Failed to load order details:', o?.id, err);
                            return of({ orderId: o?.id, data: null, adspendProducts: [] as any[] });
                          })
                        )
                    );

                    forkJoin(detailCalls)
                      .pipe(takeUntil(this.destroyed$))
                      .subscribe((results: Array<{ orderId: string; data: any; adspendProducts: any[] }>) => {
                        const mostRecent = results.find(r => r.orderId === mostRecentOrderId);
                        if (mostRecent?.data) {
                          this.orderData = mostRecent.data;
                        }

                        this.adspendProduct = results.flatMap(r => r.adspendProducts ?? []);
                        this.thisMonthsAdspend = this.calculateThisMonthsAdspend(this.adspendProduct);
                      });
                  }
                });
          }
        }
      });
  }

  getLeads() {
    if (!this.iCampaignId) return;
    let reachCampaignID = this.iCampaignId;
    this.leadsApi.getAllLeads({search: `${this.campaignData.name}` })
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.leads) {
          const dateStop = this.DateStop?.length === 10
            ? new Date(this.DateStop + 'T23:59:59.999Z')
            : new Date(this.DateStop!);

          this.leadsData = [...response.leads].filter((x: ILead) =>
            x.campaignId == reachCampaignID &&
            new Date(x.createdAt) >= new Date(this.DateStart!) &&
            new Date(x.createdAt) <= dateStop
          );
          // console.log('Filtered Leads:', this.leadsData);

          // Daily external metrics may have loaded before leads; re-apply reachLeads now.
          this.applyReachLeadsToDailyRows();
          // this.membersData = this.leadsData.filter((x: ILead) => x.isMember === true);
          // this.membersTotalCount = this.membersData.length;
        }
    });
  }

}
